import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { categoriesService } from "@/services/categoriesService";
import { mapCategoryTreeToStorefront } from "@/lib/mappers/storefront-mappers";
import { mapCategoryResponsesToAdmin } from "@/lib/mappers/category-mappers";
import type { CategoryCreateDto, CategoryUpdateDto } from "@/types/category.dto";
import { queryKeys } from "./keys";

export function useCategoryTree() {
  return useQuery({
    queryKey: queryKeys.categories.tree,
    queryFn: () => categoriesService.getTree(),
  });
}

export function useStorefrontCategories() {
  return useQuery({
    queryKey: queryKeys.categories.tree,
    queryFn: async () => {
      const tree = await categoriesService.getTree();
      return mapCategoryTreeToStorefront(tree);
    },
  });
}

export function useRootCategories() {
  return useQuery({
    queryKey: queryKeys.categories.root,
    queryFn: () => categoriesService.getRoot(),
  });
}

export function useCategoryById(id: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.categories.detail(id),
    queryFn: () => categoriesService.getById(id),
    enabled: enabled && id > 0,
  });
}

export function useAdminCategories(page = 1, pageSize = 100) {
  return useQuery({
    queryKey: queryKeys.categories.admin(page, pageSize),
    queryFn: async () => {
      const result = await categoriesService.getAll(page, pageSize, false);
      return {
        ...result,
        items: mapCategoryResponsesToAdmin(result.items),
      };
    },
  });
}

export function useCreateCategoryMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CategoryCreateDto) => categoriesService.create(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });
}

export function useUpdateCategoryMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: CategoryUpdateDto }) => categoriesService.update(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });
}

export function useDeleteCategoryMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => categoriesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });
}
